/**
 * StepPlayer — 客户端本地播 Episode.steps（深层 cursor 寻址）
 *
 * 约束（M8 §4.3）：
 *   - 不对每步 dispatch 后端
 *   - 遇 choice / minigame / cg_show → 停，交给子 Phase 处理
 *   - 遇 episode 末尾 → 停，PlayPhase 调 `/episode-completions`
 *
 * 路径寻址（严格对齐 app/core/walker.ts）：
 *   StepPlayer 支持任意合法深层 cursor 作为起点。后端 walker 在遇到嵌套决策
 *   （choice.options[k].steps / minigame.steps / if.then / if.else / cg_show.steps
 *   内部再出现 choice / minigame / cg_show）时会停在该深层 path，并把此 cursor
 *   写回 Session。跨 tab 恢复、episode 自动 route、客户端 reload 都依赖
 *   StepPlayer 从该 cursor 原样起播。
 *
 * 算法与 walker 同构（engine/cursor.ts 提供 resolve / advanceSibling /
 * popToParentSibling），区别仅在：
 *   - 前端不处理 state effect（affection / signal / butterfly / achievement）——
 *     后端已结算，前端 skip
 *   - 前端不处理 if / goto / label —— 后端 walker 已展开，cursor 永不落在这些
 *     节点上；万一落上，defensive skip
 *   - 并发组（在新 stable-ID 设计下）：父容器中遇到 group slot 时，整组打包
 *     emit；cursor 在槽位层移动，不进入组内部
 *
 * Stable step ID（spec 2026-04-26）：
 *   - cursor 段都是字符串：stable step ID / container escape key / option ID
 *   - cursor 永远停在某个 step 上（不显式经过 array 中间态）
 *   - group emit 由 player 在打包时整组发出（同一 frame 一次）
 */
import { advanceSibling, isValidPath, popToParentSibling, resolvePath, } from "./cursor.js";
const VISUAL_STEP_TYPES = new Set([
    "bg", "char_show", "char_hide", "char_look", "char_move", "bubble",
    "dialogue", "narrator", "you", "pause",
    "phone_show", "phone_hide", "text_message",
    "music_play", "music_crossfade", "music_fadeout", "sfx_play",
]);
const STATE_STEP_TYPES = new Set([
    "affection", "signal", "butterfly", "achievement",
]);
const FLOW_STEP_TYPES = new Set([
    "if", "goto", "label",
]);
const DECISION_STEP_TYPES = new Set([
    "choice", "minigame", "cg_show",
]);
// 安全阀：任一 advance() 内部 pop / skip 循环不能超过此上限。走到这里一定是
// episode 结构异常或 caller 传了 path 死循环。
const ADVANCE_SAFETY_LIMIT = 100_000;
/** 取 StepOrConcurrentGroup[]（或任意 unknown[]）首个 atomic step 的 id */
function firstStepIdOf(container) {
    if (!Array.isArray(container) || container.length === 0)
        return undefined;
    const first = container[0];
    if (Array.isArray(first)) {
        const inner = first[0];
        return inner && typeof inner === "object"
            ? inner.id
            : undefined;
    }
    if (first && typeof first === "object") {
        return first.id;
    }
    return undefined;
}
/** 在 StepOrConcurrentGroup[] 中找包含 stepId 的 group（若该 step 在某并发组里）*/
function findGroupContaining(container, stepId) {
    if (!Array.isArray(container))
        return null;
    for (const slot of container) {
        if (Array.isArray(slot)) {
            if (slot.some((s) => s && typeof s === "object" && s.id === stepId)) {
                return slot;
            }
        }
    }
    return null;
}
/** 找 path 末段 step 的父容器（StepOrConcurrentGroup[]） */
function getParentContainerForCursor(episodeSteps, path) {
    if (path.length === 0)
        return null;
    const parentPath = path.slice(0, -1);
    if (parentPath.length === 0)
        return episodeSteps;
    const parentNode = resolvePath(episodeSteps, parentPath);
    return Array.isArray(parentNode) ? parentNode : null;
}
export class StepPlayer {
    episode;
    cursor;
    done = false;
    /** 同一 group 在 advance 序列中只 emit 一次（防 cursor 落组中间） */
    emittedGroups = new WeakSet();
    constructor(episode, startCursor) {
        this.episode = episode;
        if (startCursor === undefined) {
            // 默认起点：episode.steps 第一个 atomic step
            const firstId = firstStepIdOf(episode.steps);
            this.cursor = firstId ? [firstId] : [];
        }
        else {
            this.cursor = StepPlayer.normalizeStartCursor(episode, startCursor);
        }
    }
    getCursor() {
        return [...this.cursor];
    }
    isDone() {
        return this.done;
    }
    /**
     * 推进到下一可见帧或决策点。返回 Frame；episode 末尾返回 kind=end（幂等：再次
     * 调用仍返回 end，不改 cursor）。
     */
    advance() {
        if (this.done) {
            return { kind: "end", cursor: [...this.cursor], payload: null };
        }
        if (this.cursor.length === 0) {
            this.done = true;
            return { kind: "end", cursor: [], payload: null };
        }
        let guard = 0;
        while (true) {
            if (++guard > ADVANCE_SAFETY_LIMIT) {
                throw new Error(`[StepPlayer] advance safety limit exceeded at ${JSON.stringify(this.cursor)}`);
            }
            const node = resolvePath(this.episode.steps, this.cursor);
            // 1) 容器边界 / step 不存在 → pop 到父级下一兄弟；pop 到 null 即 episode 末尾
            if (node === undefined) {
                const next = popToParentSibling(this.episode.steps, this.cursor);
                if (next === null) {
                    this.done = true;
                    return { kind: "end", cursor: [...this.cursor], payload: null };
                }
                this.cursor = next;
                continue;
            }
            // 2) escape key 末段（如 ["X","then"]）→ resolve 返 array → 进入首 step
            if (Array.isArray(node)) {
                const firstId = firstStepIdOf(node);
                if (!firstId) {
                    // 空容器 → pop 父级
                    const next = popToParentSibling(this.episode.steps, this.cursor);
                    if (next === null) {
                        this.done = true;
                        return { kind: "end", cursor: [...this.cursor], payload: null };
                    }
                    this.cursor = next;
                    continue;
                }
                this.cursor = [...this.cursor, firstId];
                continue;
            }
            if (!node || typeof node !== "object") {
                // 异常节点（null / 原始类型）—— 跳过（走 advanceSibling）
                const advanced = this.advanceCursor();
                if (advanced === null) {
                    this.done = true;
                    return { kind: "end", cursor: [...this.cursor], payload: null };
                }
                this.cursor = advanced;
                continue;
            }
            const type = node.type;
            const stepId = node.id;
            // 3) 决策步 → 停，不推进 cursor
            if (type && DECISION_STEP_TYPES.has(type)) {
                return {
                    kind: "decision",
                    cursor: [...this.cursor],
                    payload: node,
                    decisionType: type,
                };
            }
            // 4) 视觉步：先看父容器是不是把它打到 group 里 → 整组 emit
            if (type && VISUAL_STEP_TYPES.has(type) && stepId) {
                const parent = getParentContainerForCursor(this.episode.steps, this.cursor);
                const group = parent ? findGroupContaining(parent, stepId) : null;
                if (group && !this.emittedGroups.has(group)) {
                    this.emittedGroups.add(group);
                    // 推进 cursor 到 group 之后的下一 sibling slot
                    const advanced = this.advancePastGroupSlot(parent, group);
                    const frame = {
                        kind: "concurrent",
                        cursor: [...this.cursor],
                        payload: group,
                    };
                    if (advanced === null) {
                        this.done = true;
                    }
                    else {
                        this.cursor = advanced;
                    }
                    return frame;
                }
                const frame = {
                    kind: "step",
                    cursor: [...this.cursor],
                    payload: node,
                };
                const advanced = this.advanceCursor();
                if (advanced === null) {
                    this.done = true;
                }
                else {
                    this.cursor = advanced;
                }
                return frame;
            }
            // 5) state / flow-control / 未知类型 → 跳过
            if (type && (STATE_STEP_TYPES.has(type) || FLOW_STEP_TYPES.has(type))) {
                const advanced = this.advanceCursor();
                if (advanced === null) {
                    this.done = true;
                    return { kind: "end", cursor: [...this.cursor], payload: null };
                }
                this.cursor = advanced;
                continue;
            }
            // 6) 未识别 type → 兜底跳过
            const advanced = this.advanceCursor();
            if (advanced === null) {
                this.done = true;
                return { kind: "end", cursor: [...this.cursor], payload: null };
            }
            this.cursor = advanced;
        }
    }
    /**
     * 跳到给定 cursor（后端 action / completion 响应后调）。深层合法，StepPlayer
     * 会在 advance() 时正确 resolve。
     */
    jumpTo(cursor) {
        this.cursor = StepPlayer.normalizeStartCursor(this.episode, cursor);
        this.done = false;
    }
    /** 是否还能继续推进（未调 advance 前的预判；不 100% 精确，只看边界） */
    canContinue() {
        if (this.done)
            return false;
        if (this.cursor.length === 0)
            return false;
        return resolvePath(this.episode.steps, this.cursor) !== undefined
            || popToParentSibling(this.episode.steps, this.cursor) !== null;
    }
    /** advance 当前 cursor 一步；若末位则 pop；若再 pop 不动 → null */
    advanceCursor() {
        const next = advanceSibling(this.episode.steps, this.cursor);
        if (next !== null)
            return next;
        return popToParentSibling(this.episode.steps, this.cursor);
    }
    /** 推进 cursor 越过包含当前 step 的 group slot；若 slot 已是末位 → pop */
    advancePastGroupSlot(container, group) {
        const slotIndex = container.indexOf(group);
        if (slotIndex < 0)
            return this.advanceCursor();
        const nextIndex = slotIndex + 1;
        const parentPath = this.cursor.slice(0, -1);
        if (nextIndex >= container.length) {
            return popToParentSibling(this.episode.steps, this.cursor);
        }
        const nextSlot = container[nextIndex];
        let nextId;
        if (Array.isArray(nextSlot)) {
            const inner = nextSlot[0];
            nextId = inner && typeof inner === "object"
                ? inner.id
                : undefined;
        }
        else if (nextSlot && typeof nextSlot === "object") {
            nextId = nextSlot.id;
        }
        if (!nextId)
            return this.advanceCursor();
        return [...parentPath, nextId];
    }
    /**
     * cursor 合法性兜底：
     *  - `[]` 是合法的 "episode start" sentinel（spec §5 / Task 2）。直接 materialize
     *    到首 step 的 ID，不报警。这是 createSession / fresh load 的常态。
     *  - 其它非法形态（数值段、空字符串段、null）才报警 + 兜底，提示调用方有 bug。
     */
    static normalizeStartCursor(episode, cursor) {
        if (Array.isArray(cursor) && cursor.length === 0) {
            const firstId = firstStepIdOf(episode.steps);
            return firstId ? [firstId] : [];
        }
        if (!isValidPath(cursor)) {
            const firstId = firstStepIdOf(episode.steps);
            console.warn(`[StepPlayer] invalid cursor ${JSON.stringify(cursor)} — falling back to ${firstId ? `["${firstId}"]` : "[]"}`);
            return firstId ? [firstId] : [];
        }
        return [...cursor];
    }
}
